// Helpers for resolving library cover URLs.
//
// `library_items.cover_url` may contain one of:
//   - a normal external URL (https://…)
//   - an internal cached reference of the form `library-cover://{path}`
//     where {path} lives in the private `library-covers` storage bucket.
//
// Use `resolveLibraryCoverUrl` everywhere a cover image is rendered so the
// cached references are converted to short-lived signed URLs.

import { supabase } from "@/integrations/supabase/client";

const CACHED_PREFIX = "library-cover://";
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour
const cache = new Map<string, { url: string; expiresAt: number }>();

const STORAGE_BASE = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1`;

export const isCachedLibraryCover = (value: string | null | undefined): value is string =>
  !!value && value.startsWith(CACHED_PREFIX);

export const cachedLibraryCoverPath = (value: string): string =>
  value.replace(CACHED_PREFIX, "");

/**
 * Convert whatever the storage SDK returns (full URL, relative `/object/...`
 * path, or token-only payload) into a fully-qualified URL the browser can
 * load directly. Returns null if it can't make sense of the input.
 */
const toAbsoluteStorageUrl = (raw: string | null | undefined, path: string): string | null => {
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) return `${STORAGE_BASE}${raw}`;
  // Token-only fallback: build the canonical signed-object URL ourselves.
  if (raw.startsWith("?") || raw.includes("token=")) {
    const qs = raw.startsWith("?") ? raw : `?${raw}`;
    return `${STORAGE_BASE}/object/sign/library-covers/${path}${qs}`;
  }
  return null;
};

export const resolveLibraryCoverUrl = async (value: string | null | undefined): Promise<string | null> => {
  if (!value) return null;
  if (!isCachedLibraryCover(value)) return value;
  const path = cachedLibraryCoverPath(value);
  const cached = cache.get(path);
  const now = Date.now();
  if (cached && cached.expiresAt > now + 60_000) return cached.url;
  const { data, error } = await supabase.storage
    .from("library-covers")
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  // The Supabase JS SDK normalizes to `signedUrl`, but defensively accept the
  // raw `signedURL` field (the storage REST API returns the relative form).
  const raw =
    (data as { signedUrl?: string; signedURL?: string } | null)?.signedUrl
    ?? (data as { signedUrl?: string; signedURL?: string } | null)?.signedURL
    ?? null;
  const absolute = toAbsoluteStorageUrl(raw, path);
  if (error || !absolute) {
    console.warn("[libraryCovers] failed to sign cover", { path, error, raw });
    return null;
  }
  cache.set(path, { url: absolute, expiresAt: now + SIGNED_URL_TTL_SECONDS * 1000 });
  return absolute;
};

/**
 * Trigger backend caching of an external cover URL. Returns the new
 * `library-cover://…` reference so callers can update local state and
 * subsequent renders display the cached image.
 *
 * Best-effort: callers should treat failures as non-fatal — the original
 * external URL still works for display until it expires.
 */
export const cacheLibraryCover = async (
  libraryItemId: string,
  sourceUrl: string,
): Promise<{ ref: string | null; error: string | null }> => {
  if (!sourceUrl || isCachedLibraryCover(sourceUrl)) return { ref: null, error: null };
  try {
    const { data, error } = await supabase.functions.invoke("library-cover-cache", {
      body: { library_item_id: libraryItemId, source_url: sourceUrl },
    });
    if (error) {
      console.warn("[libraryCovers] cache request failed", error);
      return { ref: null, error: error.message ?? "Cover cache request failed" };
    }
    const next = (data as { cover_url?: string } | null)?.cover_url ?? null;
    return { ref: next, error: null };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn("[libraryCovers] cache exception", msg);
    return { ref: null, error: msg };
  }
};

/**
 * Upload a user-supplied cover image (already resized client-side) for a
 * library item. Returns the new `library-cover://…` reference on success.
 */
export const uploadLibraryCover = async (
  libraryItemId: string,
  file: File,
): Promise<{ ref: string | null; error: string | null }> => {
  try {
    const form = new FormData();
    form.append("library_item_id", libraryItemId);
    form.append("file", file, file.name);
    const { data, error } = await supabase.functions.invoke("library-cover-upload", {
      body: form,
    });
    if (error) {
      console.warn("[libraryCovers] upload failed", error);
      return { ref: null, error: error.message ?? "Cover upload failed" };
    }
    const next = (data as { cover_url?: string } | null)?.cover_url ?? null;
    return { ref: next, error: next ? null : "Upload returned no cover_url" };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn("[libraryCovers] upload exception", msg);
    return { ref: null, error: msg };
  }
};
